import React, {useEffect, useState, useContext} from 'react';
import {useRouter} from 'next/router';
import NewsService from '../../services/news.service';
import TNHContext from '@contexts/TNHContext';
import styles from './News.module.css';
import {getLocaleContent} from '@helper/locale-content.js';
import Link from 'next/link';
import {changeToSlug} from '@helper/string.helper';
import SwiperCore, {A11y, Autoplay, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);

export default function News() {
    const {locale} = useRouter();
    const {pressTalkTnhId} = useContext(TNHContext);
    const [newsCategories, setNewsCategories] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [topNews, setTopNews] = useState([]);
    const top = 10;

    useEffect(() => {
        if (pressTalkTnhId) {
            // Fetch only if pressTalkTnhId is not null
            const newsService = new NewsService();
            newsService
                .getNewsCategoryList({locale})
                .then(response => {
                    let categories = [
                        {news_category_name: getLocaleContent(locale, 'common.all'), news_category_id: null},
                        ...response.categoryList,
                    ];

                    // Exclude the category with pressTalkTnhId
                    categories = categories.filter(category => category.news_category_id !== pressTalkTnhId);

                    setNewsCategories(categories);

                    if (categories.length > 0) {
                        fetchTopNews(categories[0].news_category_id, pressTalkTnhId, top, locale);
                    }
                })
                .catch(error => {
                    console.error('Error fetching news categories:', error);
                });
        }
    }, [locale, pressTalkTnhId]);

    const handleButtonClick = index => {
        setActiveIndex(index);
        const selectedCategory = newsCategories[index];
        if (selectedCategory) {
            fetchTopNews(selectedCategory.news_category_id, pressTalkTnhId, top, locale);
        }
    };

    const fetchTopNews = (categoryId, pressTalkId, top, locale) => {
        const newsService = new NewsService();
        newsService
            .getTopNewsByCategoryId({id: categoryId, pressTalkId, top, locale})
            .then(response => {
                setTopNews(response);
                console.log(response);
            })
            .catch(error => {
                console.error('Error fetching top news:', error);
            });
    };

    const formatDate = timestamp => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return (
        <>
            <div className={styles['section-container']}>
                <section className="container">
                    <header>
                        <h2 className={styles['title']}>
                            <span>{getLocaleContent(locale, 'page.news.title1')}</span>{' '}
                            {getLocaleContent(locale, 'page.news.title2')}{' '}
                            <span>{getLocaleContent(locale, 'page.news.title3')}</span>
                        </h2>
                        <p className={styles['lead']}>{getLocaleContent(locale, 'page.news.description')}</p>
                    </header>

                    <div className={styles['tabs-container']}>
                        <Swiper
                            className={styles['tabs-swiper']}
                            slidesPerView="auto"
                            freeMode={true}
                            breakpoints={{
                                0: {
                                    spaceBetween: 10,
                                },
                                1024: {
                                    spaceBetween: 24,
                                },
                            }}>
                            {newsCategories.map((category, index) => (
                                <SwiperSlide key={category.news_category_id ?? index} className={styles['tab-slide']}>
                                    <button
                                        type="button"
                                        className={`${styles['pill']} ${
                                            index === activeIndex ? styles['is-active'] : ''
                                        }`}
                                        onClick={() => handleButtonClick(index)}
                                        aria-pressed={index === activeIndex}>
                                        {category.news_category_name}
                                    </button>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <Link href="/tin-tuc">
                            <a className={`${styles['view-all']} ${styles['view-all-desktop']}`}>
                                {getLocaleContent(locale, 'common.see-all')}{' '}
                                <svg className={styles['icon']} viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M8 5l8 7-8 7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </Link>
                    </div>
                    <div className={styles['container']}>
                        {/* Main News */}
                        <Link
                            href={{
                                pathname: '/[slug]',
                                query: {slug: `${changeToSlug(topNews[0]?.news_title)}-d${topNews[0]?.news_id}`},
                            }}>
                            <div className={styles['main-news']}>
                                <img src={topNews[0]?.image_url} alt="Main news" className={styles['news-image']} />
                                <div className={styles['news-content']}>
                                    <div style={{display: 'flex', alignItems: 'baseline', marginBottom: '8px'}}>
                                        <p className={styles['category']}>{topNews[0]?.news_category_name}</p>
                                        <p className={styles['date']}>
                                            {topNews[0]?.news_date ? formatDate(topNews[0].news_date) : ''}
                                        </p>
                                    </div>
                                    <h2 className={styles['title']}>{topNews[0]?.news_title}</h2>
                                    <p className={styles['description']}>{topNews[0]?.short_description}</p>
                                </div>
                            </div>
                        </Link>

                        {/* Side News List */}

                        <div className={styles['side-news']}>
                            {topNews.slice(1, 4).map((news, idx) => (
                                <Link
                                    key={news.news_id ?? idx}
                                    href={{
                                        pathname: '/[slug]',
                                        query: {slug: `${changeToSlug(news.news_title)}-d${news.news_id}`},
                                    }}>
                                    <div className={styles['side-card']}>
                                        <div className={styles['side-image-wrapper']}>
                                            <img
                                                src={news.image_url}
                                                alt={news.news_title}
                                                className={styles['side-image']}
                                            />
                                        </div>
                                        <div className={styles['side-content']}>
                                            <div
                                                style={{display: 'flex', alignItems: 'baseline', marginBottom: '15px'}}>
                                                <p className={styles['category']}>{news.news_category_name}</p>
                                                <p className={styles['date']}>
                                                    {news.news_date ? formatDate(news.news_date) : ''}
                                                </p>
                                            </div>
                                            <h3 className={styles['title']}>{news.news_title}</h3>
                                            <p className={styles['description']}>{news.short_description}</p>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <Link href="/tin-tuc">
                            <a className={`${styles['view-all']} ${styles['view-all-mobile']}`}>
                                {getLocaleContent(locale, 'common.see-all')}{' '}
                                <svg className={styles['icon']} viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M8 5l8 7-8 7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </Link>
                    </div>
                </section>
            </div>
        </>
    );
}
